/** Двойной тап: второе касание не позже этого срока после первого. */
const DOUBLE_TAP_MS = 300;
/** …и не дальше этого от него. Тот же порог отделяет тап от протяжки: касание, уехавшее дальше, —
 *  свайп, и следующее за ним касание начинает счёт заново, а не увеличивает кадр. */
const DOUBLE_TAP_PX = 40;

export interface PinchHandlers {
  /** Накопленный масштаб, уже зажатый в [min, max]. */
  onScale?: (scale: number) => void;
  /** Сдвиг центра касаний с прошлого события, px. Работает и одним пальцем, и двумя. */
  onPan?: (dx: number, dy: number) => void;
  onDoubleTap?: (point: { x: number; y: number }) => void;
  /** Поворот двух пальцев с прошлого события, градусы (по часовой — положительный). */
  onRotate?: (deltaDeg: number) => void;
  /**
   * Текущий масштаб на стороне потребителя. Читается в момент, когда лёг второй палец: масштаб меняют
   * и мимо щипка (двойной тап, повторное открытие экрана), и без этого жест стартовал бы от своего
   * устаревшего значения — фото прыгало бы к нему с первого движения.
   */
  scale?: () => number;
  min?: number;
  /** Потолок масштаба; функция читается на каждом движении — потолок «100 % пикселей» зависит от кадра. */
  max?: number | (() => number);
}

/**
 * Щипок, протяжка и двойной тап на pointer-событиях. Возвращает функцию отписки.
 * Движение и отпускание слушаются на окне: палец часто уходит за пределы элемента, а pointer capture
 * в jsdom нет вовсе.
 */
export function attachPinch(el: HTMLElement, handlers: PinchHandlers = {}): () => void {
  const { onScale, onPan, onDoubleTap, onRotate, scale: currentScale, min = 1, max = 4 } = handlers;
  const maxScale = (): number => Math.max(min, typeof max === 'function' ? max() : max);
  const points = new Map<number, { x: number; y: number }>();
  let scale = min;
  let baseScale = min;
  let baseSpread = 0;
  let lastCenter: { x: number; y: number } | null = null;
  let lastAngle = 0;
  let lastTapAt = 0;
  let tapStart: { x: number; y: number } | null = null;
  let tapMoved = false;

  const center = () => {
    let x = 0;
    let y = 0;
    for (const p of points.values()) {
      x += p.x;
      y += p.y;
    }

    return { x: x / points.size, y: y / points.size };
  };

  const spread = () => {
    const [a, b] = [...points.values()];
    return Math.hypot(a.x - b.x, a.y - b.y);
  };

  const angle = () => {
    const [a, b] = [...points.values()];
    return (Math.atan2(b.y - a.y, b.x - a.x) * 180) / Math.PI;
  };

  const onDown = (e: PointerEvent) => {
    points.set(e.pointerId, { x: e.clientX, y: e.clientY });
    lastCenter = center();
    if (points.size === 2) {
      baseSpread = spread();
      lastAngle = angle();
      baseScale = currentScale?.() ?? scale;
    } else if (points.size === 1) {
      const now = Date.now();
      const near = !!tapStart && Math.hypot(e.clientX - tapStart.x, e.clientY - tapStart.y) <= DOUBLE_TAP_PX;
      if (near && !tapMoved && now - lastTapAt <= DOUBLE_TAP_MS) {
        lastTapAt = 0;
        onDoubleTap?.({ x: e.clientX, y: e.clientY });
      } else {
        lastTapAt = now;
      }

      tapStart = { x: e.clientX, y: e.clientY };
      tapMoved = false;
    }
  };

  const onMove = (e: PointerEvent) => {
    if (!points.has(e.pointerId)) return;
    points.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (tapStart && !tapMoved && Math.hypot(e.clientX - tapStart.x, e.clientY - tapStart.y) > DOUBLE_TAP_PX) {
      tapMoved = true;
    }

    const next = center();
    if (lastCenter) onPan?.(next.x - lastCenter.x, next.y - lastCenter.y);
    lastCenter = next;
    if (points.size === 2 && baseSpread > 0) {
      scale = Math.min(maxScale(), Math.max(min, (baseScale * spread()) / baseSpread));
      onScale?.(scale);
      const next = angle();
      // Переход через ±180° — тот же малый поворот, а не разворот на целый круг.
      const delta = ((next - lastAngle + 540) % 360) - 180;
      lastAngle = next;
      if (delta) onRotate?.(delta);
    }
  };

  const onUp = (e: PointerEvent) => {
    if (!points.delete(e.pointerId)) return;
    baseSpread = 0;
    lastCenter = points.size ? center() : null;
  };

  /**
   * Щипок на трекпаде приходит не касаниями: браузер отдаёт его колесом с `ctrlKey` (так же ведёт себя
   * эмуляция устройства в DevTools). Без этой ветки на ноутбуке масштаб не менялся вовсе.
   */
  const onWheel = (e: WheelEvent) => {
    if (!e.ctrlKey || !onScale) return;
    e.preventDefault();
    // Экспонента от дельты: один и тот же шаг колеса даёт один и тот же множитель на любом масштабе.
    scale = Math.min(maxScale(), Math.max(min, (currentScale?.() ?? scale) * Math.exp(-e.deltaY / 100)));
    onScale(scale);
  };

  el.addEventListener('pointerdown', onDown as EventListener);
  el.addEventListener('wheel', onWheel as EventListener, { passive: false });
  window.addEventListener('pointermove', onMove as EventListener);
  window.addEventListener('pointerup', onUp as EventListener);
  window.addEventListener('pointercancel', onUp as EventListener);

  return () => {
    el.removeEventListener('pointerdown', onDown as EventListener);
    el.removeEventListener('wheel', onWheel as EventListener);
    window.removeEventListener('pointermove', onMove as EventListener);
    window.removeEventListener('pointerup', onUp as EventListener);
    window.removeEventListener('pointercancel', onUp as EventListener);
    points.clear();
  };
}
